import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import StarRating from "@/components/seo/StarRating";

interface Country {
  id: string;
  name: string;
  city?: string | null;
  flag_url?: string | null;
  banner_url?: string | null;
  tagline?: string | null;
  tourist_stay_default?: string | null;
  remote_work_visa_option?: string | null;
  key_requirements?: string | null;
  tax_residency_trigger?: string | null;
  notes_1_5_months?: number | null;
  ease_1_5_months?: number | null;
  ease_compliance?: number | null;
  cost_of_living?: number | null;
  attractiveness_rating?: number | null;
  employee_notes?: string | null;
  legal_compliance_risks?: string | null;
  hr_guidelines?: string | null;
  cost_impact_estimate?: string | null;
  company_caps_observed?: string | null;
  weekly_cost_excluding_flight?: number | null;
  round_trip_flight_cost_from_france?: number | null;
  image_url?: string | null;
  renest_notes?: string | null;
  total_score?: number | null;
}

interface CompanyCountryDetailDialogProps {
  country: Country | null;
  open: boolean;
  onClose: () => void;
}

const CompanyCountryDetailDialog = ({ country, open, onClose }: CompanyCountryDetailDialogProps) => {
  if (!country) return null;

  const InfoRow = ({ label, value }: { label: string; value: string | null }) => (
    <div className="flex justify-between items-center py-3 border-b">
      <span className="text-sm font-medium text-muted-foreground">{label}</span>
      <span className="text-sm font-semibold">{value || "Non spécifié"}</span>
    </div>
  );

  const RatingRow = ({ label, rating }: { label: string; rating: number | null }) => (
    <div className="flex justify-between items-center py-3 border-b">
      <span className="text-sm font-medium text-muted-foreground">{label}</span>
      {rating ? <StarRating rating={rating} showText={true} /> : <span className="text-sm">Non évalué</span>}
    </div>
  );

  const TextSection = ({ label, value }: { label: string; value: string | null }) => {
    if (!value) return null;
    return (
      <div className="py-3 border-b">
        <span className="text-sm font-medium text-muted-foreground block mb-2">{label}</span>
        <p className="text-sm whitespace-pre-wrap">{value}</p>
      </div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto p-0">
        {/* Banner Image */}
        {(country.image_url || country.banner_url) && (
          <div className="w-full h-48 overflow-hidden">
            <img
              src={country.image_url || country.banner_url || ""}
              alt={country.name}
              className="w-full h-full object-cover"
            />
          </div>
        )}

        {/* Header */}
        <div className="px-6 pt-6">
          <div className="flex items-start gap-4 mb-4">
            {country.flag_url && (
              <img
                src={country.flag_url}
                alt={`Drapeau ${country.name}`}
                className="w-16 h-16 object-cover rounded"
              />
            )}
            <div className="flex-1">
              <h2 className="text-3xl font-bold mb-2">{country.name}</h2>
              {country.city && (
                <p className="text-lg text-muted-foreground mb-1">{country.city}</p>
              )}
              {country.tagline && (
                <p className="text-lg text-muted-foreground italic">"{country.tagline}"</p>
              )}
            </div>
          </div>

          {country.total_score !== null && (
            <div className="flex items-center gap-4 mb-6">
              <Badge variant="secondary" className="text-lg px-4 py-2">
                Score total: {country.total_score}/20
              </Badge>
              <StarRating rating={Math.round((country.total_score / 20) * 5)} size="lg" />
            </div>
          )}
        </div>

        <Separator />

        <Tabs defaultValue="employee" className="w-full">
          <TabsList className="w-full px-6">
            <TabsTrigger value="employee" className="flex-1">Info Employés</TabsTrigger>
            <TabsTrigger value="company" className="flex-1">Info Entreprise</TabsTrigger>
          </TabsList>

          <TabsContent value="employee" className="px-6 py-4">
            <h3 className="text-xl font-semibold mb-4">Informations clés</h3>
            
            <div className="space-y-1">
              <InfoRow 
                label="Séjour touristique (par défaut)" 
                value={country.tourist_stay_default} 
              />
              <InfoRow 
                label="Visa remote / durée" 
                value={country.remote_work_visa_option} 
              />
              <TextSection
                label="Exigences clés"
                value={country.key_requirements}
              />
              <InfoRow 
                label="Enregistrement / Règles fiscales" 
                value={country.tax_residency_trigger} 
              />
            </div>

            <Separator className="my-6" />

            <h3 className="text-xl font-semibold mb-4">Évaluations</h3>
            
            <div className="space-y-1">
              <RatingRow 
                label="Notes 1–5m" 
                rating={country.notes_1_5_months} 
              />
              <RatingRow 
                label="Ease 1–5m" 
                rating={country.ease_1_5_months} 
              />
              <RatingRow 
                label="Ease Compliance" 
                rating={country.ease_compliance} 
              />
              <RatingRow 
                label="Coût de vie" 
                rating={country.cost_of_living} 
              />
              <RatingRow 
                label="Attractivité cadres" 
                rating={country.attractiveness_rating} 
              />
            </div>

            <Separator className="my-6" />

            <h3 className="text-xl font-semibold mb-4">Notes employés</h3>
            <TextSection
              label="Informations pratiques"
              value={country.employee_notes}
            />

            <Separator className="my-6" />

            <h3 className="text-xl font-semibold mb-4">Coûts estimés</h3>
            <div className="space-y-1">
              <InfoRow 
                label="Coût hebdo (hors vol)" 
                value={country.weekly_cost_excluding_flight ? `${country.weekly_cost_excluding_flight} €` : null} 
              />
              <InfoRow 
                label="A/R depuis FR (est.)" 
                value={country.round_trip_flight_cost_from_france ? `${country.round_trip_flight_cost_from_france} €` : null} 
              />
            </div>

            {country.renest_notes && (
              <>
                <Separator className="my-6" />
                <h3 className="text-xl font-semibold mb-4">Renest Notes</h3>
                <TextSection
                  label="Informations complémentaires"
                  value={country.renest_notes}
                />
              </>
            )}
          </TabsContent>

          <TabsContent value="company" className="px-6 py-4">
            <h3 className="text-xl font-semibold mb-4">Informations entreprise</h3>
            
            <div className="space-y-4">
              <TextSection
                label="Impact coût (entreprise)"
                value={country.cost_impact_estimate}
              />
              <TextSection
                label="Risques juridiques"
                value={country.legal_compliance_risks}
              />
              <TextSection
                label="Lignes directrices RH"
                value={country.hr_guidelines}
              />
              <TextSection
                label="Caps d'entreprise (observés)"
                value={country.company_caps_observed}
              />
              <TextSection
                label="Notes Renest"
                value={country.renest_notes}
              />
              
              {!country.cost_impact_estimate && 
               !country.legal_compliance_risks && 
               !country.hr_guidelines && 
               !country.company_caps_observed && 
               !country.renest_notes && (
                <p className="text-muted-foreground text-center py-8">
                  Aucune information entreprise disponible pour ce pays
                </p>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};

export default CompanyCountryDetailDialog;
